import { truncateAddress } from "@aptos-labs/ts-sdk";
import styled from "@emotion/styled";
import { Flex } from "@radix-ui/themes";
import { observer } from "mobx-react-lite";
import type React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import type { ColorTypes } from "@themes/colors";
import { theme } from "@themes/themes";

import AptosIcon from "@assets/icons/wallets/aptos.svg?react";
import FundingIcon from "@assets/icons/wallets/funding.svg?react";
import TradingIcon from "@assets/icons/wallets/trading.svg?react";

import { useStores } from "@stores";
import type { Token } from "@stores/BalanceStore";
import { DIALOG_TYPE } from "@stores/DialogStore";

import { BN } from "@utils/BN";
import { formatUsd } from "@utils/Format";

import { Button, Image, Text } from "@src/ui-kit";

import { IS_APTOS_MAINNET } from "@constants";
import { Icon } from "./Icon";

interface SideBarMainProps {
  handleDeposit: () => void;
}

export const SideBarMain: React.FC<SideBarMainProps> = observer(({ handleDeposit }) => {
  const { t } = useTranslation();
  const { balanceStore, accountStore, toastStore, dialogStore } = useStores();
  const navigate = useNavigate();
  const [isBalancesOpen, setIsBalancesOpen] = useState(true);
  // const wallet = accountStore.wallet?.account;
  const tokens = balanceStore.vaultBalances.filter((token) => token.amount.gt(BN.ZERO));
  const loading = balanceStore.loading;

  const totalBalance = tokens.reduce((acc, t) => acc.plus(BN.fromString(t.amountUsd)), BN.ZERO);
  const totalBalanceFormat = formatUsd(totalBalance, { decimals: 2, toSignificant: false });

  // Get individual account balances
  const getFundingBalance = () => {
    if (!accountStore.fundingSubAccount?.address) return "$0.00";
    return balanceStore.getFormattedUSDCVaultBalanceForUser(accountStore.fundingSubAccount.address);
  };

  const getTradingBalance = (tradingAddress: string) => {
    const balance = balanceStore.getFormattedUSDCVaultBalanceForUser(tradingAddress);
    return balance;
  };

  const getAptosRootBalance = () => {
    if (!accountStore.wallet?.account?.address) return "$0.00";
    const usdcToken = balanceStore.externalAccountBalances.find((token) => token.symbol === "USDC");
    return usdcToken ? formatUsd(BN.fromString(usdcToken.amountUsd)) : "$0.00";
  };

  const shouldShowAptosAccount = () => {
    const walletName = accountStore.wallet?.wallet?.name?.toLowerCase() || "";
    return walletName.includes("metamask") || walletName.includes("google") || walletName.includes("apple");
  };

  const handleCopy = (address?: string) => {
    if (!address) return;
    navigator.clipboard.writeText(address);
    toastStore.show({
      type: "success",
      title: t("wallet.copySuccess"),
      content: t("wallet.copyMessage"),
    });
  };

  const handleOpenExplorer = (address: string | undefined) => {
    if (address) {
      window.open(`https://explorer.aptoslabs.com/account/${address}?network=testnet`, "_blank");
    }
  };
  // const handleDisconnect = () => {
  //   accountStore.disconnect();
  // };

  const handleGetTokens = async () => {
    balanceStore.getTestTokens();
  };

  const handleWithdraw = () => {
    dialogStore.open(DIALOG_TYPE.WITHDRAW);
  };

  const handleTransfer = (fromAccount?: string) => {
    dialogStore.open(DIALOG_TYPE.TRANSFER, { transferFrom: fromAccount });
  };

  const handleAddTradingAccount = async () => {
    try {
      await accountStore.addTradingAccount();
    } catch {}
  };

  const handleOpenApiKeys = () => {
    dialogStore.close(DIALOG_TYPE.ASSETS);
    navigate("/api-keys");
  };

  const handleSelectTradingAccount = (address: string, _e: React.MouseEvent) => {
    accountStore.setActiveTradingAccount(address);
  };

  return (
    <Panel data-testid="sidebar:panel">
      <ScrollableContent data-testid="sidebar:scroll">
        {shouldShowAptosAccount() && accountStore.wallet?.account && (
          <AccountCard data-testid="sidebar:aptos:card" style={{ marginBottom: 12 }}>
            <Flex align="center" gap="8px">
              <Flex direction="column" gap="2px">
                <Flex align="center" direction="row" gap="5px">
                  <AptosIcon />
                  <Text color="gray-11" font="support10">
                    {t("wallet.aptosAccount")}
                  </Text>
                </Flex>
                <AmountText data-testid="sidebar:aptos:balance">{getAptosRootBalance()}</AmountText>
              </Flex>
            </Flex>
            <Flex align="center" direction="column" gap="4px" width="120px">
              <IconSquareButton
                data-testid="sidebar:aptos:copy"
                variant="classic"
                fullwidth
                onClick={() => handleCopy(accountStore.wallet?.account?.address?.toString())}
              >
                <Flex align="center" gap="5px">
                  <Icon color="gray-11" icon="ri-file-copy-line" size={12} />
                  <Text color="gray-11" font="support10">
                    {truncateAddress(accountStore.wallet?.account?.address?.toString() || "") || t("common.unknown")}
                  </Text>
                </Flex>
              </IconSquareButton>
              <IconSquareButton
                data-testid="sidebar:aptos:explorer"
                style={{ width: "100%" }}
                variant="classic"
                onClick={() => handleOpenExplorer(accountStore.wallet?.account?.address?.toString())}
              >
                <Icon color="gray-11" icon="ri-share-box-line" size={12} />
                <Text color="gray-11" font="support10">
                  {t("common.explorer")}
                </Text>
              </IconSquareButton>
            </Flex>
          </AccountCard>
        )}
        <TopTitle data-testid="sidebar:balances:toggle" onClick={() => setIsBalancesOpen((v) => !v)}>
          <Flex align="center" gap="8px">
            <Icon color="gray-11" icon={isBalancesOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} size={16} />
            <Text data-testid="sidebar:balances:title" color="gray-11" font="body12">
              {t("wallet.ekidenBalances")}
            </Text>
          </Flex>
          <Flex align="center" gap="8px">
            <Text data-testid="sidebar:balances:total-label" color="gray-11" font="body12">
              {t("wallet.totalBalance")}
            </Text>
            <Text data-testid="sidebar:balances:total" font="body14">
              {totalBalanceFormat}
            </Text>
          </Flex>
        </TopTitle>
        {isBalancesOpen && (
          <>
            <Flex data-testid="sidebar:balances:list" direction="column" gap="12px" width="100%">
              {accountStore.fundingSubAccount && (
                <AccountCard data-testid="sidebar:funding:card">
                  <Flex align="center" gap="8px">
                    <Flex direction="column" gap="2px">
                      <Flex align="center" direction="row" gap="5px">
                        <FundingIcon />
                        <Text color="gray-11" font="support10">
                          {t("depositWithdraw.fundingAccount")}
                        </Text>
                      </Flex>
                      <AmountText data-testid="sidebar:funding:balance">{getFundingBalance()}</AmountText>
                    </Flex>
                  </Flex>
                  <Flex align="center" direction="column" gap="4px" width="120px">
                    <IconSquareButton
                      data-testid="sidebar:funding:copy"
                      variant="classic"
                      fullwidth
                      onClick={() => handleCopy(accountStore.fundingSubAccount?.address)}
                    >
                      <Flex align="center" gap="5px">
                        <Icon color="gray-11" icon="ri-file-copy-line" size={12} />
                        <Text color="gray-11" font="support10">
                          {truncateAddress(accountStore.fundingSubAccount?.address || "") || t("common.unknown")}
                        </Text>
                      </Flex>
                    </IconSquareButton>
                    <Flex align="center" gap="4px" width="100%">
                      <IconSquareButton
                        data-testid="sidebar:funding:transfer"
                        style={{ width: 84 }}
                        variant="classic"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTransfer("funding");
                        }}
                      >
                        <Icon color="gray-11" icon="ri-shuffle-line" size={12} />
                        <Text color="gray-11" font="support10">
                          {t("common.transfer")}
                        </Text>
                      </IconSquareButton>
                      <IconSquareButton
                        data-testid="sidebar:funding:explorer"
                        variant="classic"
                        onClick={() => handleOpenExplorer(accountStore.fundingSubAccount?.address)}
                      >
                        <Icon color="gray-11" icon="ri-share-box-line" size={12} />
                      </IconSquareButton>
                    </Flex>
                  </Flex>
                </AccountCard>
              )}

              {accountStore.tradingSubAccounts.map((tradingAccount, index) => {
                const isActive = accountStore.activeTradingAccountAddress === tradingAccount.address;
                return (
                  <AccountCard
                    data-testid={`sidebar:trading:${index + 1}:card`}
                    key={tradingAccount.address}
                    active={isActive}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectTradingAccount(tradingAccount.address, e);
                    }}
                  >
                    <Flex align="center" gap="8px">
                      <Flex direction="column" gap="2px">
                        <Flex align="center" direction="row" gap="5px">
                          <TradingIcon />
                          <Text color="gray-11" font="support10">
                            {t("depositWithdraw.tradingAccount", { index: index + 1 })}
                          </Text>
                          {isActive && (
                            <Badge data-testid={`sidebar:trading:${index + 1}:active`}>{t("common.active")}</Badge>
                          )}
                        </Flex>
                        <AmountText data-testid={`sidebar:trading:${index + 1}:balance`}>
                          {getTradingBalance(tradingAccount.address)}
                        </AmountText>
                      </Flex>
                    </Flex>
                    <Flex align="center" direction="column" gap="4px" width="120px">
                      <IconSquareButton
                        data-testid={`sidebar:trading:${index + 1}:copy`}
                        variant="classic"
                        fullwidth
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCopy(tradingAccount.address);
                        }}
                      >
                        <Flex align="center" gap="5px">
                          <Icon color="gray-11" icon="ri-file-copy-line" size={12} />
                          <Text color="gray-11" font="support10">
                            {truncateAddress(tradingAccount.address) || t("common.unknown")}
                          </Text>
                        </Flex>
                      </IconSquareButton>
                      <Flex align="center" gap="4px" width="100%">
                        <IconSquareButton
                          data-testid={`sidebar:trading:${index + 1}:transfer`}
                          style={{ width: 84 }}
                          variant="classic"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTransfer(`trading-${index}`);
                          }}
                        >
                          <Icon color="gray-11" icon="ri-shuffle-line" size={12} />
                          <Text color="gray-11" font="support10">
                            Transfer
                          </Text>
                        </IconSquareButton>
                        <IconSquareButton
                          data-testid={`sidebar:trading:${index + 1}:explorer`}
                          variant="classic"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenExplorer(tradingAccount.address);
                          }}
                        >
                          <Icon color="gray-11" icon="ri-share-box-line" size={12} />
                        </IconSquareButton>
                      </Flex>
                    </Flex>
                  </AccountCard>
                );
              })}

              <Button
                data-testid="sidebar:trading:create"
                style={{ height: "auto", padding: "10px 0" }}
                variant="classic"
                fullwidth
                onClick={handleAddTradingAccount}
              >
                <Text font="button">{t("wallet.createTradingAccount")}</Text>
              </Button>
            </Flex>

            <ActionsGrid>
              <Button
                data-testid="sidebar:actions:deposit"
                size="4"
                style={{ paddingBottom: "8px", height: "auto" }}
                variant="surface"
                onClick={handleDeposit}
              >
                <Flex direction="column">
                  <Icon icon="ri-add-line" />
                  <Text font="button">{t("common.deposit")}</Text>
                </Flex>
              </Button>
              {!IS_APTOS_MAINNET && (
                <Button
                  data-testid="sidebar:actions:get-test-tokens"
                  loading={balanceStore.isTestTokensRequested}
                  size="4"
                  style={{ paddingBottom: "8px", height: "auto" }}
                  variant="surface"
                  onClick={handleGetTokens}
                >
                  <Flex direction="column">
                    <Icon icon="ri-coin-line" />
                    <Text font="button">{t("wallet.getTestTokens")}</Text>
                  </Flex>
                </Button>
              )}
              <Button
                data-testid="sidebar:actions:withdraw"
                size="4"
                style={{ paddingBottom: "8px", height: "auto" }}
                variant="surface"
                onClick={handleWithdraw}
              >
                <Flex direction="column">
                  <Icon icon="ri-arrow-down-fill" />
                  <Text font="button">{t("common.withdraw")}</Text>
                </Flex>
              </Button>
              <Button
                data-testid="sidebar:actions:transfer"
                size="4"
                style={{ paddingBottom: "8px", height: "auto" }}
                variant="surface"
                onClick={() => handleTransfer()}
              >
                <Flex direction="column">
                  <Icon icon="ri-shuffle-line" />
                  <Text font="button">{t("common.transfer")}</Text>
                </Flex>
              </Button>
              <Button
                data-testid="sidebar:actions:api-keys"
                size="4"
                style={{ paddingBottom: "8px", height: "auto" }}
                variant="surface"
                onClick={handleOpenApiKeys}
              >
                <Flex direction="column">
                  <Icon icon="ri-key-2-line" />
                  <Text font="button">{t("apiKeys.shortTitle")}</Text>
                </Flex>
              </Button>
            </ActionsGrid>
          </>
        )}

        <Flex data-testid="sidebar:assets:list" direction="column">
          <AssetsHeader>
            <Text color="gray-11" font="body14">
              {t("wallet.yourAssets")}
            </Text>
          </AssetsHeader>
          {tokens.length === 0 && loading && (
            <Text data-testid="sidebar:assets:loading" color="gray-8" font="body14" style={{ padding: 16 }}>
              {t("wallet.loadingBalances")}
            </Text>
          )}
          {tokens.length === 0 && !loading && (
            <Text data-testid="sidebar:assets:empty" color="gray-8" font="body14" style={{ padding: 16 }}>
              {t("wallet.noAssets")}
            </Text>
          )}
          {tokens.map((token) => (
            <TokenRowItem key={token.symbol} token={token} />
          ))}
        </Flex>
      </ScrollableContent>

      {/* Fixed logout button at bottom */}
      <LogoutButtonContainer>
        <Button
          data-testid="sidebar:logout"
          variant="classic"
          onClick={() => {
            accountStore.disconnect();
            dialogStore.close(DIALOG_TYPE.ASSETS);
          }}
        >
          <Icon icon="ri-logout-box-line" />
          <Text font="button">{t("common.logOut")}</Text>
        </Button>
      </LogoutButtonContainer>
    </Panel>
  );
});

interface TokenRowItemProps {
  token: Token;
  valueColor?: ColorTypes;
}

const TokenRowItem: React.FC<TokenRowItemProps> = ({ token, valueColor }) => (
  <TokenRow data-testid={`sidebar:asset:${token.symbol}`}>
    <Flex align="center" gap="8px">
      <Image alt={`${token.symbol} Logo`} height="24px" src={token.icon} width="24px" />
      <Flex direction="column" gap="2px">
        <Text font="body16">{token.symbol}</Text>
        <Text color="gray-11" font="body14">
          {token.name}
        </Text>
      </Flex>
    </Flex>
    <Flex align="end" direction="column" gap="2px">
      <Text data-testid={`sidebar:asset:${token.symbol}:amount`} font="body16">
        {token.amountFormatted}
      </Text>
      <Text
        data-testid={`sidebar:asset:${token.symbol}:usd`}
        color={(valueColor as ColorTypes) || "green-strong"}
        font="body14"
      >
        {formatUsd(BN.fromString(token.amountUsd))}
      </Text>
    </Flex>
  </TokenRow>
);

const Panel = styled.div`
  background: ${theme.color("gray-2")};
  border-left: 1px solid ${theme.color("accent-7")};
  position: absolute;
  right: 0;
  z-index: 97;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ScrollableContent = styled.div`
  flex: 1;
  height: 100vh;
  padding: 16px;
  overflow-y: auto;
  overflow-x: hidden;
`;

const LogoutButtonContainer = styled.div`
  position: sticky;
  height: 120px;
  bottom: 0;
  padding: 16px;
  background: ${theme.color("gray-2")};
  border-top: 1px solid ${theme.color("gray-5")};
  margin-top: auto;
  text-align: right;
`;

const TopTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
`;

// Deprecated layout block; kept for reference if needed again
// const Header = styled.div`
//   display: flex;
//   justify-content: space-between;
//   margin-bottom: 20px;
//
//   padding: 12px;
//
//   border-bottom: 1px solid ${theme.color("accent-5")};
// `;

const TokenRow = styled.div`
  display: flex;
  justify-content: space-between;

  padding: 16px;

  border-bottom: 1px solid ${theme.color("gray-5")};
`;

const AssetsHeader = styled.div`
  padding: 16px 16px 8px;
  border-bottom: 1px solid ${theme.color("accent-5")};
`;

const AmountText = styled(Text)`
  font-size: 22px;
`;

// const WalletIcon = styled.img`
//   width: 20px;
//   height: 20px;
//   border-radius: 100%;
// `;

const RowCard = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid ${theme.color("gray-5")};
  border-radius: 8px;
  padding: 6px;
`;

const AccountCard = styled(RowCard)<{ active?: boolean }>`
  padding: 12px;
  border-color: ${({ active }) => (active ? theme.color("accent-7") : theme.color("gray-5"))};
  box-shadow: ${({ active }) => (active ? `0 0 0 2px ${theme.color("accent-4")}` : "none")};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${theme.color("accent-6")};
    background-color: ${theme.color("gray-3")};
  }

  &[style*="cursor: pointer"]:hover {
    transform: translateY(-1px);
  }
`;

// const Badge = styled.div`
//   background: ${theme.color("green-weak")};
//   color: ${theme.color("green-strong")};
//   border: 1px solid ${theme.color("green-medium")};
//   padding: 2px 6px;
//   border-radius: 6px;
//   font-size: 12px;
// `;

const IconSquareButton = styled(Button)`
  min-width: 32px;
  min-height: 32px;
  padding: 5px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  & > * {
    pointer-events: none;
  }
`;

const ActionsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  padding-top: 12px;
`;

const Badge = styled.div`
  background: ${theme.color("green-weak")};
  color: ${theme.color("green-strong")};
  border: 1px solid ${theme.color("green-medium")};
  padding: 2px 6px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
`;

// const RowCardExpanded = styled(RowCard)`
//   background: ${theme.color("gray-contrast")};
//   border-top-left-radius: 0;
//   border-top-right-radius: 0;
//   border-top: none;
// `;

// const RowGroup = styled.div`
//   display: flex;
//   flex-direction: column;
//   gap: 0;
// `;

// const CheckBadge = styled.div`
//   width: 20px;
//   height: 20px;
//   border-radius: 50%;
//   display: inline-flex;
//   align-items: center;
//   justify-content: center;
//   border: 1px solid ${theme.color("green-medium")};
//   background: ${theme.color("green-weak")};
// `;

// const Connector = styled.div`
//   display: flex;
//   flex-direction: column;
//   align-items: left;
//   padding-left: 12px;
//
//   .cap {
//     margin-top: -1px;
//     width: 10px;
//     height: 0.5px;
//     background: ${theme.color("accent-9")};
//   }
//
//   .stemCap {
//     margin-left: 4px;
//     width: 2px;
//     height: 2px;
//     background: ${theme.color("accent-9")};
//   }
//
//   .stem {
//     margin-left: 4px;
//     width: 2px;
//     height: 2px;
//     background: ${theme.color("accent-9")};
//     margin-top: 2px;
//   }
// `;
